<template>
  <div class="get-started" @click.stop @mousedown.stop @focusin="refreshMapCentre">
    <div class="get-started__intro">
      <span class="get-started__icon" aria-hidden="true">
        <span class="material-icons">ads_click</span>
      </span>
      <div class="get-started__text">
        <h3 class="get-started__title">{{ $t('plan_trail.get_started.title') }}</h3>
        <ol class="get-started__steps">
          <li>{{ $t('plan_trail.get_started.step_start') }}</li>
          <li>{{ $t('plan_trail.get_started.step_draw') }}</li>
          <li>{{ $t('plan_trail.get_started.step_reshape') }}</li>
        </ol>
      </div>
    </div>

    <div class="get-started__or" role="separator">
      <span>{{ $t('plan_trail.get_started.or') }}</span>
    </div>

    <div class="get-started__fields">
      <PlaceSearchPanel
        ref="startField"
        variant="field"
        :icon="startIcon"
        :focus="startFocus"
        :placeholder="startLabel ?? $t('plan_trail.get_started.search_start')"
        :label="$t('plan_trail.get_started.start')"
        :load-places="noPlaces"
        @pick="pick => pickFor('start', pick)"
      >
        <template v-if="offersCurrentLocation('start', locationUsedFor)" #trailing>
          <button
            type="button"
            class="get-started__locate"
            :class="{ 'get-started__locate--busy': locating === 'start' }"
            :title="$t('plan_trail.get_started.use_my_location')"
            :aria-label="$t('plan_trail.get_started.use_my_location')"
            :disabled="!!locating"
            @click="useMyLocation('start')"
          >
            <span class="material-icons" aria-hidden="true">my_location</span>
          </button>
        </template>
      </PlaceSearchPanel>
      <PlaceSearchPanel
        ref="endField"
        :key="startKey ?? 'no-start'"
        variant="field"
        :icon="finishIcon"
        :focus="endFocus"
        :disabled="!startCoord"
        :placeholder="startCoord ? $t('plan_trail.get_started.search_end') : $t('plan_trail.get_started.start_first')"
        :label="$t('plan_trail.get_started.end')"
        :load-places="loadNearStart"
        @pick="pick => pickFor('end', pick)"
      >
        <template v-if="startCoord && offersCurrentLocation('end', locationUsedFor)" #trailing>
          <button
            type="button"
            class="get-started__locate"
            :class="{ 'get-started__locate--busy': locating === 'end' }"
            :title="$t('plan_trail.get_started.use_my_location')"
            :aria-label="$t('plan_trail.get_started.use_my_location')"
            :disabled="!!locating"
            @click="useMyLocation('end')"
          >
            <span class="material-icons" aria-hidden="true">my_location</span>
          </button>
        </template>
      </PlaceSearchPanel>
      <p v-if="locationError" class="get-started__hint" role="status">{{ $t('plan_trail.get_started.location_failed') }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { useStore } from 'vuex';
import PlaceSearchPanel from './PlaceSearchPanel.vue';
import { ServiceProvider } from '../services/ServiceProvider';
import { endpointSearchFocus, offersCurrentLocation, startFieldLabel } from '../services/get-started';
import { currentLocation } from '../utils/currentLocation';
import { i18n } from '../../../i18n';
import { moveEndpointToPick } from '../services/endpoint-picks';
import { addressKey, nearbyPlaces, rememberedAddresses as addressCache } from '../services/nearby-places';
import { PlacePick, pickCoordinates, pickName } from '../services/place-picks';
import { reversePlaceLabel } from '../../../utils/services/PlaceSearchService';
import startIcon from '../../../images/planner/route-start.png';
import finishIcon from '../../../images/planner/route-finish.png';

/**
 * The left column of a plan that hasn't been started — no routes, or one
 * route with no line yet (`routeKey`): how to draw one on the map, or a
 * search for where it starts and ends. Picking a start puts the route's
 * first point there (creating the route when there's none); picking an end
 * then draws the line to it, and the column hands over to the route's
 * endpoint rows (RouteEndpoints.vue).
 */

type Coord = [number, number];

const props = defineProps<{ routeKey?: string | null }>();
const store = useStore();

const startField = ref<InstanceType<typeof PlaceSearchPanel> | null>(null);
const endField = ref<InstanceType<typeof PlaceSearchPanel> | null>(null);

const route = computed(() => (props.routeKey ? store.state.planTrail?.routes?.[props.routeKey] : null));
const startCoord = computed<Coord | null>(() => {
  const coords = route.value?.markers?.[0]?.markerGeometry?.coordinates;
  return coords ? [coords[0], coords[1]] : null;
});
const startKey = computed(() => (startCoord.value ? addressKey(startCoord.value) : null));

/** The start as it was picked here — its name is known without a lookup. */
const pickedStart = ref<{ key: string; name: string } | null>(null);
/** The start's address once looked up — a start clicked on the map has only coordinates. */
const startAddress = ref<string | null>(null);

watch(startKey, async (key) => {
  startAddress.value = null;
  if (!key || !startCoord.value) return;
  if (addressCache.has(key)) startAddress.value = addressCache.get(key) ?? null;
  if (pickedStart.value?.key === key) {
    // Picked here, so its name is known — and the start's in; the end is next.
    await nextTick();
    endField.value?.focusInput();
    return;
  }
  if (addressCache.has(key)) return;
  const [lon, lat] = startCoord.value;
  const address = await reversePlaceLabel(lon, lat);
  addressCache.set(key, address);
  if (startKey.value === key) startAddress.value = address;
}, { immediate: true });

const startLabel = computed(() => startFieldLabel({
  coordinates: startCoord.value,
  pickedName: pickedStart.value?.key === startKey.value ? pickedStart.value?.name : null,
  address: startAddress.value,
}));

/** Where the map is looking — read as a field is used, since the map moves. */
const mapCentre = ref<Coord | null>(null);
function refreshMapCentre() {
  try {
    const centre = ServiceProvider.getInstance().getMapService().getCenter();
    mapCentre.value = centre ? [centre[0], centre[1]] : null;
  } catch {
    mapCentre.value = null;
  }
}

const startFocus = computed(() => endpointSearchFocus(startCoord.value, mapCentre.value));
const endFocus = computed(() => endpointSearchFocus(startCoord.value, mapCentre.value));

/** Nothing is "near" a start that doesn't exist yet. */
const noPlaces = () => Promise.resolve([]);
function loadNearStart() {
  return startCoord.value ? nearbyPlaces([startCoord.value]) : Promise.resolve([]);
}

/** "Use my location" — on whichever field hasn't had it (see offersCurrentLocation). */
const locationUsedFor = ref<'start' | 'end' | null>(null);
const locating = ref<'start' | 'end' | null>(null);
const locationError = ref(false);

async function useMyLocation(which: 'start' | 'end') {
  if (locating.value) return;
  locating.value = which;
  locationError.value = false;
  try {
    const coordinates = await currentLocation();
    locationUsedFor.value = which;
    await pickFor(which, { kind: 'address', name: i18n.global.t('plan_trail.get_started.my_location'), coordinates });
  } catch {
    locationError.value = true;
  } finally {
    locating.value = null;
  }
}

async function pickFor(which: 'start' | 'end', pick: PlacePick) {
  const routeKey = props.routeKey ?? await store.dispatch('planTrail/addNewRoute');
  if (which === 'start') {
    pickedStart.value = { key: addressKey(pickCoordinates(pick)), name: pickName(pick) };
  }
  await moveEndpointToPick(store.dispatch, { routeKey, which, dayNumber: route.value?.day ?? 1, pick });
}

defineExpose({ pickFor, useMyLocation });
</script>

<style scoped>
.get-started {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  padding: 0.25rem 1rem 1rem;
}

.get-started__intro {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.9rem;
  border-radius: 12px;
  background: rgba(253, 223, 0, 0.07);
  border: 1px solid rgba(253, 223, 0, 0.18);
}

.get-started__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #fddf00;
  color: #17181d;
}

.get-started__icon .material-icons {
  font-size: 20px;
}

.get-started__text {
  min-width: 0;
}

.get-started__title {
  margin: 0 0 0.25rem;
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1.25;
  color: #fff;
}

.get-started__steps {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  margin: 0;
  padding: 0;
  list-style: none;
  counter-reset: step;
  font-size: 0.78rem;
  line-height: 1.35;
  color: #c6c8cf;
}

.get-started__steps li {
  display: flex;
  align-items: baseline;
  gap: 0.45rem;
}

.get-started__steps li::before {
  content: counter(step);
  counter-increment: step;
  flex-shrink: 0;
  min-width: 1.1rem;
  font-size: 0.68rem;
  font-weight: 700;
  color: #fddf00;
}

.get-started__or {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8a8d96;
}

.get-started__or::before,
.get-started__or::after {
  content: '';
  flex: 1;
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
}

.get-started__fields {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.get-started__locate {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: none;
  color: #a1a4ad;
  cursor: pointer;
  transition: color 0.15s, background 0.15s;
}

.get-started__locate .material-icons {
  font-size: 18px;
}

.get-started__locate:hover:not(:disabled) {
  color: #fddf00;
  background: rgba(255, 255, 255, 0.08);
}

.get-started__locate:disabled {
  cursor: default;
  opacity: 0.6;
}

.get-started__locate--busy .material-icons {
  animation: get-started-pulse 1s ease-in-out infinite;
}

@keyframes get-started-pulse {
  50% { opacity: 0.35; }
}

.get-started__hint {
  margin: 0;
  font-size: 0.72rem;
  color: #e08a8a;
}
</style>
